import SqOneTrustModule, { ALERT_BOX_CLOSED_COOKIE_KEY } from '@square/one-trust';
import Cookies from 'js-cookie';
// This fixes a unknown TS issue in @square/one-trust. For some reason the types are exported as the default property of the SqOneTrust class.
// This caused issues where the typeof SqOneTrust and the instance of SqOneTrust were not the same.
// This TS hack fixes the problem but it is likely to break when/if the root cause is fixed
const SqOneTrust = SqOneTrustModule;
export var OneTrustConsent;
(function (OneTrustConsent) {
    OneTrustConsent["STRICTLY_NECESSARY"] = "C0001";
    OneTrustConsent["PERFORMANCE"] = "C0002";
    OneTrustConsent["FUNCTIONAL"] = "C0003";
    OneTrustConsent["TARGETING"] = "C0004";
})(OneTrustConsent || (OneTrustConsent = {}));
export class EnvoyConsentHeader {
    'X-Block-Cookies';
    'X-Allow-Cookies';
}
export const X_ALLOW_COOKIES = 'X-Allow-Cookies';
export const X_BLOCK_COOKIES = 'X-Block-Cookies';
export const X_BLOCK_COOKIES_DEFAULT_VALUE = 'true';
export const X_CSRF_Token = 'X-CSRF-Token';
export const POST_NEW_ONE_TRUST_INSTANCE = 'dashboardSqOneTrustInstanceChangedEvent';
export const ONE_TRUST_ALERT_COOKIE = 'OptanonAlertBoxClosed';
function getOneTrustActiveGroups() {
    return window.OnetrustActiveGroups;
}
function getOneTrustConsentModel() {
    return window.OneTrust?.GetDomainData?.()?.ConsentModel?.Name;
}
export function hasConsentBeenSet() {
    // `getOneTrustActiveGroups()` will only be undefined if the OneTrust script has not run.
    const noConsentFound = !getOneTrustActiveGroups();
    // If the merchant has anything other than strictly necessary cookies, they have either selected consent or been auto-consented (US/CA).
    const hasAutoConsented = /C000[^1]/.test(getOneTrustActiveGroups());
    // If the merchant is in an opt-out region, they will be auto-consented
    const isInOptOutRegion = getOneTrustConsentModel() === 'opt-out';
    // If neither of those are true, and there is an `OptanonAlertBoxClosed` cookie, the merchant has consented through the modal.
    const hasManuallyConsented = document.cookie.includes(ONE_TRUST_ALERT_COOKIE);
    return Boolean(noConsentFound || hasAutoConsented || isInOptOutRegion || hasManuallyConsented);
}
export function hasConsent(consentLevel) {
    if (!consentLevel) {
        throw new Error('Consent level must be provided for OneTrust. Consent levels are available in `@squareup/service-one-trust`.');
    }
    // Default to true if NODE_ENV is testing
    return (
    // eslint-disable-next-line square/no-process-env-in-libraries
    process.env.NODE_ENV === 'test' ||
        window.SqOneTrust?.groupConsentHash[consentLevel] === '1');
}
export function generateEnvoyConsentHeader() {
    // eslint-disable-next-line square/no-process-env-in-libraries
    if (process.env.NODE_ENV === 'test') {
        // NODE_ENV is testing
        return {};
    }
    const envoyConsentDefaultHeader = {
        [X_BLOCK_COOKIES]: X_BLOCK_COOKIES_DEFAULT_VALUE,
    };
    // If the onetrust script ran, oneTrustActivityGroups will always be populated with C0001
    const activeGroups = getOneTrustActiveGroups();
    if (hasConsentBeenSet() && activeGroups) {
        // X-Block-Cookies is always included to inform server that we need to filter cookies based
        // on X-Allow-Cookies categories.
        return {
            ...envoyConsentDefaultHeader,
            [X_ALLOW_COOKIES]: activeGroups,
        };
    }
    return envoyConsentDefaultHeader;
}
/**
 * Execute a callback when user grants the specified consent level.
 * If consent is already granted, the callback executes immediately.
 * Returns a cleanup function to remove the event listener.
 *
 * @param consentLevel The consent level to wait for
 * @param callback Function to execute when consent is granted
 * @returns Cleanup function to remove event listener
 */
export function onFirstConsent(consentLevel, callback) {
    if (hasConsent(consentLevel)) {
        callback();
        return () => { }; // No-op cleanup since callback already executed
    }
    function executeAfterOneTrust() {
        // Resolve if consent has been granted. Otherwise keep listeners waiting.
        if (hasConsent(consentLevel)) {
            callback();
            document.removeEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
        }
    }
    document.addEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
    // Return cleanup function
    return () => {
        document.removeEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
    };
}
/**
 * Execute a callback when user rejects the specified consent level.
 * If consent is already rejected and has been set, the callback executes immediately.
 * Returns a cleanup function to remove the event listener.
 *
 * @param consentLevel The consent level to wait for rejection
 * @param callback Function to execute when consent is rejected
 * @returns Cleanup function to remove event listener
 */
export function onFirstRejection(consentLevel, callback) {
    if (!hasConsent(consentLevel) && hasConsentBeenSet()) {
        callback();
        return () => { }; // No-op cleanup since callback already executed
    }
    function executeAfterOneTrust() {
        if (!hasConsent(consentLevel) && hasConsentBeenSet()) {
            callback();
            document.removeEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
        }
    }
    document.addEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
    // Return cleanup function
    return () => {
        document.removeEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
    };
}
/**
 * Returns a promise that resolves to true if consent is granted, false if rejected.
 * The promise will resolve when the user makes a choice about the specified consent level.
 *
 * @param consentLevel The consent level to wait for
 * @returns Promise that resolves to true if consent granted, false if rejected
 */
export function getConsent(consentLevel) {
    return new Promise((resolve) => {
        // Use object to avoid prefer-const lint error while allowing mutual cleanup
        const cleanup = {
            consent: undefined,
            rejection: undefined,
        };
        cleanup.consent = onFirstConsent(consentLevel, () => {
            if (cleanup.rejection) {
                cleanup.rejection();
            }
            resolve(true);
        });
        cleanup.rejection = onFirstRejection(consentLevel, () => {
            if (cleanup.consent) {
                cleanup.consent();
            }
            resolve(false);
        });
    });
}
/**
 * Opens the OneTrust cookie preferences modal where users can manage their consent settings.
 */
export function showOneTrustModal() {
    window.OneTrust?.ToggleInfoDisplay();
}
/**
 * Returns whether the user is located in the European Union.
 * @returns true if user is in EU, false otherwise, undefined if OneTrust not loaded
 */
export function isInEU() {
    return window.SqOneTrust?.userLocatedInEU;
}
/**
 * Custom event fired by our host app (e.g dashboard) when window.SqOneTrust instance change.
 * This happens when the the cookie preferences are changed in the host app.
 */
export const SQ_ONE_TRUST_INSTANCE_CHANGE = 'dashboardSqOneTrustInstanceChangedEvent';
/**
 * These are the default values of the cookie preferences in the event
 * where window.SqOneTrust is not present. When not present, it is
 * assumed that the host app does not have OneTrust integration, and it
 * is safe to allow all cookies.
 */
export const FUNCTIONALITY_COOKIES_BLOCKED_DEFAULT = false;
export const ANALYTICS_COOKIES_BLOCKED_DEFAULT = false;
export const RETARGETING_COOKIES_BLOCKED_DEFAULT = false;
export const NECESSARY_COOKIES_BLOCKED_DEFAULT = false;
export const GROUP_CONSENT_HASH_DEFAULT = {
    C0001: '1',
    C0002: '1',
    C0003: '1',
    C0004: '1',
};
/**
 * This class provides helpers cookie preferences for GDPR compliance by
 * extracting information from window.SqOneTrust that is set by our host
 * app, such as Dashboard. An instance of this should be created in
 * MessengerModel.
 * A user can set their preferences for 4 different types of cookies:
 * - Strictly necessary
 * - Functionality
 * - Analytics / tracking
 * - Retargeting / advertizing
 */
export default class SqOneTrustService {
    /**
     * Subset of the SqOneTrust instance that is always kept in-sync with the one
     * in window. If window.SqOneTrust is not present, the properties will
     * remain in its default values.
     */
    _sqOneTrust = {
        strictlyNecessaryCookiesBlocked: NECESSARY_COOKIES_BLOCKED_DEFAULT,
        functionalityCookiesBlocked: FUNCTIONALITY_COOKIES_BLOCKED_DEFAULT,
        performanceAndAnalyticsCookiesBlocked: ANALYTICS_COOKIES_BLOCKED_DEFAULT,
        retargetingOrAdvertisingCookiesBlocked: RETARGETING_COOKIES_BLOCKED_DEFAULT,
        groupConsentHash: GROUP_CONSENT_HASH_DEFAULT,
    };
    constructor() {
        // Sets up SqOneTrust for local development
        if (!window.SqOneTrust) {
            window.SqOneTrust = new SqOneTrust();
        }
        this._populatePreferenceFromWindow();
        document.addEventListener(SQ_ONE_TRUST_INSTANCE_CHANGE, () => {
            this._ensureAlertBoxCookieExists();
            // This needs to create a new instance of SqOneTrust every time consent changes
            window.SqOneTrust = new SqOneTrust();
            this._populatePreferenceFromWindow();
        });
    }
    /**
     * Populate our instance of SqOneTrust with the one in window. This is
     * called when the instance is first created, and subsequently when
     * the event SQ_ONE_TRUST_INSTANCE_CHANGE is triggered by our host apps.
     */
    _populatePreferenceFromWindow = () => {
        if (window.SqOneTrust) {
            this._sqOneTrust.strictlyNecessaryCookiesBlocked = window.SqOneTrust.strictlyNecessaryCookiesBlocked;
            this._sqOneTrust.functionalityCookiesBlocked = window.SqOneTrust.functionalityCookiesBlocked;
            this._sqOneTrust.performanceAndAnalyticsCookiesBlocked =
                window.SqOneTrust.performanceAndAnalyticsCookiesBlocked;
            this._sqOneTrust.retargetingOrAdvertisingCookiesBlocked =
                window.SqOneTrust.retargetingOrAdvertisingCookiesBlocked;
            this._sqOneTrust.groupConsentHash = window.SqOneTrust.groupConsentHash;
        }
    };
    /**
     * Certain browser extensions "I don't care about cookies" for example, broke header loading after consent.
     * This sets the cookie `OptanonAlertBoxClosed` if an external script is blocking the Onetrust modal.
     * https://github.com/squareup/appointments/blob/c2195edd19c45b163b37373e221a2d87fbe9a6d7/merchant/app/instance-initializers/onetrust.js#L35
     */
    _ensureAlertBoxCookieExists = () => {
        const existingCookie = Cookies.get(ALERT_BOX_CLOSED_COOKIE_KEY);
        if (existingCookie) {
            return;
        }
        const oneTrustSdkElement = document.getElementById('onetrust-consent-sdk');
        const isHidden = oneTrustSdkElement ? window.getComputedStyle(oneTrustSdkElement).visibility === 'hidden' : false;
        if (isHidden) {
            Cookies.set(ALERT_BOX_CLOSED_COOKIE_KEY, new Date().toISOString());
        }
    };
    /**
     * Get the SqOneTrust instance as a Proxy so that it can be stored elsewhere in
     * the code while still keeping it updated whenever we re-populate from window
     * since we are passing by reference. However, as we intercept the setter here,
     * no where outside this controller can mutate the properties of the instance.
     */
    getSqOneTrust = () => {
        function getter(target, key) {
            if (key === 'strictlyNecessaryCookiesBlocked') {
                return target.strictlyNecessaryCookiesBlocked;
            }
            if (key === 'functionalityCookiesBlocked') {
                return target.functionalityCookiesBlocked;
            }
            if (key === 'performanceAndAnalyticsCookiesBlocked') {
                return target.performanceAndAnalyticsCookiesBlocked;
            }
            if (key === 'retargetingOrAdvertisingCookiesBlocked') {
                return target.retargetingOrAdvertisingCookiesBlocked;
            }
            return false;
        }
        return new Proxy(this._sqOneTrust, {
            get: getter,
            set: getter,
        });
    };
    /**
     * Get a list of headers to append to requests. Documentation can be found here:
     * https://docs.google.com/document/d/141w7iDe38ygMR8wqeJuPH0rGJ-EyG4QUs3AN4o4K9jE/edit#
     * Essentially, X-Block-Cookies is a backend flag for whether GDPR should be
     * enforced at all. X-Allow-Cookies will be present once the user consents
     * to any type of cookie (at a minimum, this includes strictly necessary, i.e. C0001).
     */
    getCookieFilterHeaders = () => {
        let allowHeaderValue = '';
        const allowedCookies = [];
        for (const cookieName of Object.keys(this._sqOneTrust.groupConsentHash)) {
            if (this._sqOneTrust.groupConsentHash[cookieName] === '1') {
                // '1' = true (cookie type is allowed)
                allowedCookies.push(cookieName);
            }
        }
        allowHeaderValue = allowedCookies.join(', ');
        return {
            'X-Allow-Cookies': allowHeaderValue,
            'X-Block-Cookies': 'true',
        };
    };
}
